/* #region(collapsed) DEVELOPER DOCUMENTATION ════════════════════════════════════ */
/**
  @license
  • COPYRIGHT NOTICE:
    © CROUIN 2021. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  • INTRODUCTION:
    2-in-1 module (cart page / cart modal) with a shared set of settings.
    The cart features mostly rely on HTML custom elements: modal, cart-controller, cart-ui and cart-item.
    All methods can be accessed with the javascript Class MaxDesign (see "popular javascript methods" bellow).
 
  • POPULAR JAVASCRIPT METHODS:
    const controller = MaxDesign.cart.controller; // get cart controller
    const cart = MaxDesign.cart.UI; // get cart UI
    const cartJson = controller.settings.cart; // get cart json data
    controller.editCart('add|change|update', data, btn, form); // modify the cart (pass form HTMLElement, or data object)
    controller.render(await controller.fetch()); // render updated cart (async function)
    controller.modal.open(); // open cart modal
    document.addEventListener('cart:add', console.log); // new item added to cart with request response
 
  • MORE INFORMATION:
    - Contact us at admin(at)maxdesign.expert
    - Visit shopify cart API https://shopify.dev/api/ajax/reference/cart
*/
/* #endregion ════════════════════════════════════════════════════════════════════ */

window.MaxDesign = window.MaxDesign || class {};
MaxDesign.cart = {
  get controller() {
    return document.getElementById('cmd');
  },
  get UI() {
    return document.getElementById('cmd-ui');
  },
};

/* #region CART CONTROLLER ═══════════════════════════════════════════════════════ */
class CartController extends HTMLElement {
  async connectedCallback() {
    this.aborter = new AbortController();
    this.settings = this.json(this);
    this.sectionId = document.querySelector('.cart-maxdesign').id.slice(16);
    this.modal = MaxDesign?.modal?.cart;
    await Promise.all(['cart-ui-maxdesign', 'cart-item-maxdesign'].map(el => customElements.whenDefined(el)));
    document.addEventListener('click', this.onFormSubmit.bind(this), { capture: true, signal: this.aborter.signal });
    document.addEventListener('cart:add', this.onCartAdd.bind(this), { signal: this.aborter.signal });
  }

  disconnectedCallback() {
    this.aborter.abort();
  }

  /* #region(collapsed) ON EVENT ═══════════════════════════════════════════════════ */

  /**
   * On form submission with event bubbling for dynamic ATC.
   * Conditional step to check if form is valid
   */
  async onFormSubmit(e) {
    if (this.preventFormSubmit) return;
    const submitButtons = document.querySelectorAll(this.settings.submitButtons);
    for (let btn of submitButtons) {
      const form = btn.form || btn.closest('form[action*="/cart/add"]');
      if (btn.contains(e.target) && !btn.disabled && form && this.isFormValid(form)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        await this.editCart('add', undefined, btn, form);
        break;
      }
    }
  }

  onCartAdd() {
    const modals = MaxDesign?.modal?.controller?.modals;
    modals && modals.forEach(modal => modal.state === 'open' && modal.dataset.id !== 'cart' && modal.close());
  }

  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) AUXILIARY METHODS ══════════════════════════════════════════ */

/**
 * Add, change or update cart from form HTMLElement or data object
 * @param {string} endpoint - Either add, change or update
 * @param {Object} data - Form element and/or data based on endpoint
 * @param {HTMLElement} btn - Button element triggering the request
 * @param {HTMLElement} form - Form element
 * @param {Boolean} scroll - Whether to scroll to the cart modal after the request
 */
async editCart(endpoint, data = {}, btn, form, scroll = true) {
  /*
  // ✅ ORIGINAL (à remettre si besoin)
  try {
    this.loading = { el: btn || this, load: true };
    const formData = new FormData(form);
    Object.keys(data).forEach(key => this.formData(formData, key, data[key]));
    const groupId = formData.get('properties[_id]');
    const discount = formData.get('discount');
    if (groupId) await this.editGroup(groupId);
    if (discount) await this.editDiscounts({ add: discount, render: false });
    await this.handleCartRequest(`cart/${endpoint}.js`, formData);
    this.modal?.open();
  } catch (error) {
    alert(`${error.message}: ${error.description}`);
    console.error(error);
  } finally {
    if (endpoint == 'add' && this.modal && scroll) this.modal.scroll = {};
    this.loading = { el: btn || this, load: false };
  }
  */

  // ✅ MODIFIÉ : si "data-cart-redirect" est actif, on redirige vers /cart au lieu d’ouvrir le modal
  try {
    this.loading = { el: btn || this, load: true };
    const formData = new FormData(form);
    Object.keys(data).forEach(key => this.formData(formData, key, data[key]));
    const groupId = formData.get('properties[_id]');
    const discount = formData.get('discount');

    if (groupId) await this.editGroup(groupId);
    if (discount) await this.editDiscounts({ add: discount, render: false });

    await this.handleCartRequest(`cart/${endpoint}.js`, formData);

    // 👉 Ajout au panier = redirection vers la page panier (si option activée)
    if (endpoint === 'add' && this.hasAttribute('data-cart-redirect')) {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    // Sinon, comportement normal : ouverture du cart modal MaxDesign
    this.modal?.open();
  } catch (error) {
    alert(`${error.message}: ${error.description}`);
    console.error(error);
  } finally {
    // Ne pas scroller le modal si on redirige
    if (endpoint == 'add' && this.modal && scroll && !this.hasAttribute('data-cart-redirect')) {
      this.modal.scroll = {};
    }
    this.loading = { el: btn || this, load: false };
  }
}


  async editGroup(id) {
    if (new URLSearchParams(location.search).has('group')) history.replaceState(null, '', location.pathname);
    const cart = await this.cart();
    const itemsToRemove = cart.items.filter(({ properties }) => properties?._id == id);
    if (itemsToRemove.length) {
      const updates = itemsToRemove.reduce((obj, item) => ({ ...obj, [item.key]: 0 }), {});
      await this.editCart('update', { updates });
    }
  }

  /**
   * Retrieve active codes and add/remove new code(s), then render cart
   * We handle code errors by comparing old and new cart attribute
   * ! In case cart caching prevents correct discount preview, consider updating a random cart attribute to reset cache
   * @param {Object} codes - object with add and remove key, with comma separated codes
   * @param {String} route - url route
   * @param {Object} btn - discount submit btn
   */
  async editDiscounts({ add = '', remove = '', render = true, btn }) {
    try {
      this.loading = { el: btn, load: true };
      const route = Shopify.routes.root;
      const cart = await (await fetch(route + 'cart.js')).json();
      const active = new Set(cart.items.flatMap(item => item.discounts.map(({ title }) => title)));
      const discounts = [...new Set([...active, ...add.split(',')].filter(d => !remove.split(',').includes(d)))];
      await fetch(`${route}checkout?discount=${discounts.join(',') || 'clear'}`).then(async () => {
        if (!render) return;
        this.render(await this.fetch());
        const UI = MaxDesign.cart.UI;
        const cart = await (await fetch(route + 'cart.js')).json();
        const active = new Set(cart.items.flatMap(item => item.discounts.map(({ title }) => title.toLowerCase())));
        const codesNotAdded = add.split(',').filter(code => !active.has(code.toLowerCase()) && code.trim() !== '');
        if (codesNotAdded.length > 0) {
          const codePlaceholder = UI.DOM.discount.text.querySelector('span[data-code]');
          codePlaceholder && (codePlaceholder.dataset.code = codesNotAdded.join(', '));
          UI.DOM.discount.text.hidden = false;
          UI.DOM.discount.input.style.setProperty('--color-discount-field', 'var(--color-critical-error)');
        }
      });
    } catch (error) {
      console.error(error);
      this.loading = { el: btn, load: false };
    }
  }

  /**
   * fetch cart page using section rendering API
   * @returns {Object} cart markup string
   */
  async fetch() {
    const url = `${Shopify.routes.root}cart?section_id=${this.sectionId}`;
    return fetch(url)
      .then(response => response.text())
      .catch(error => {
        console.error(error);
        location.href = `${Shopify.routes.root}cart`;
      });
  }

  /**
   * Handles common functionality for making requests to the cart
   * @param {string} endpoint - API endpoint for the request
   * @param {Object} formData - FormData object containing the request data
   */
  async handleCartRequest(endpoint, formData) {
    const route = Shopify.routes.root;
    this.formData(formData, 'sections', this.sectionId);
    this.formData(formData, 'sections_url', route + 'cart');
    const response = await fetch(route + endpoint, { method: 'POST', body: formData });
    const result = await response.json();
    if (response.status >= 300) throw result;
    this.event(endpoint.replace('/', ':').replace('.js', ''), result);
    this.render(result.sections[this.sectionId]);
  }

  /**
   * Render cart section passed as parameter
   * Replace existing UI or append it in controller otherwise
   * We preserve sticky and timer state to avoid repetitive animation on cart change
   * @param {String} response - cart markup as text
   */
  render(response) {
    const newSection = new DOMParser().parseFromString(response, 'text/html').querySelector('.cart-maxdesign');
    const newUI = newSection.querySelector('#cmd-ui');
    const currentUI = MaxDesign.cart.UI;
    if (currentUI) {
      // remove current modal slots
      [...this.parentElement.children].forEach(child => child.getAttribute('slot') === 'modal' && child.remove());
      // Preserve some attributes and elements to improve transitions
      ['data-sticky', 'data-timer'].forEach(attr => newUI.toggleAttribute(attr, currentUI?.hasAttribute(attr)));
      ['.cmd-timer', '.cmd-shipping', '.cmd-offer__progress', '[data-recommendations-result]'].forEach(selector => {
        const el = currentUI?.querySelector(selector);
        el && newUI.querySelector(selector)?.replaceWith(el);
      });
      // replace UI
      currentUI.replaceWith(newUI);
    } else this.append(newUI);
    this.event('cart:render', newUI.settings.cart);
  }

  formData(formData, key, data) {
    if (data === Object(data) || Array.isArray(data)) {
      for (const i in data) this.formData(formData, `${key}[${i}]`, data[i]);
    } else (data || data === 0) && formData.append(key, data);
  }

  /**
   * check form validation.
   * @param {Object} form.
   */
  isFormValid(form) {
    if (form.checkValidity()) return true;
    const firstInvalidInput = form.querySelector(':invalid');
    if (firstInvalidInput) firstInvalidInput.focus();
    return false;
  }

  event(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }
  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) GETTERS & SETTERS ══════════════════════════════════════════ */

  /**
   * set loader animation when loading discount, cart or checkout
   */
  set loading({ el, load }) {
    if (!el) return;
    if (!el.childElementCount) el.innerHTML = `<span>${el.textContent}</span>`;
    el.dataset.loader = load ? 'loading' : '';
  }

  /**
   * allows cart to override all listeners, except for bundle and upsell modules
   */
  get preventFormSubmit() {
    const bundle = MaxDesign?.bundles?.bundle;
    const productUpsellActive = MaxDesign?.product?.upsell?.activeUpsells?.length;
    return bundle || productUpsellActive;
  }

  /**
   * @returns {Object} custom element settings
   */
  get json() {
    return scope => JSON.parse(scope.querySelector(':scope > [type="application/json"]')?.innerText || '{}');
  }

  /**
   * Get cart json data
   * @returns {Object} cart json data
   */
  get cart() {
    return async () => {
      const url = `${Shopify.routes.root}cart.js`;
      try {
        return await fetch(url).then(response => response.json());
      } catch (error) {
        console.error(error);
      }
    };
  }

  /**
   * Clear cart
   */
  get clear() {
    return async () => {
      const url = `${Shopify.routes.root}cart/clear.js`;
      try {
        return await fetch(url, { method: 'POST' }).then(response => response.json());
      } catch (error) {
        console.error(error);
      }
    };
  }

  /* #endregion ══════════════════════════════════════════════════════════════════ */
}
/* #endregion ════════════════════════════════════════════════════════════════════ */

/* #region CART UI ═══════════════════════════════════════════════════════════════ */
class CartUI extends HTMLElement {
  async connectedCallback() {
    this.aborter = new AbortController();
    this.controller = MaxDesign.cart.controller;
    this.settings = this.controller.json(this);
    this.setVariables();
    this.updateCount();
    await customElements.whenDefined('cart-item-maxdesign');
    if (this.settings.cart.item_count > 0) this.init();
    else this.controller.loading = { el: this.controller, load: false };
  }

  disconnectedCallback() {
    this.aborter.abort();
  }

  async init() {
    this.onInit(); /* on cart init (no user interaction) */
    this.onSubmitCart(); /* button loader on cart submit */
    this.onInputDiscount(); /* update hidden discount input on keyup */
    this.onSubmitDiscount(); /* apply discount on cart page */
    this.onRemoveDiscount(); /* remove discount promotion on close action */
    this.onATCBtnIntersect(); /* toggle fixed button */
    this.onStickyBtnClick(); /* delegate submit click */
    this.onSubmitGiftMessage(); /* add a personalized message card */
    this.onSubmitGiftWrapping(); /* wrap any cart item */
  }

  /* #region(collapsed) SET VARIABLES ══════════════════════════════════════════════ */
  setVariables() {
    this.prices = {
      subtotal: this.settings.cart.original_total_price,
      total: this.settings.cart.total_price,
      discount: this.settings.cart.total_discount,
      shipping: this.settings.shipping.price.value,
    };

    this.discount = {
      field: this.settings.discount.field,
      qtyBreak: this.settings.discount.qtyBreak,
    };

    this.offers = this.settings.offers;

    this.DOM = {
      counters: this.settings.cartCountSelectors && document.querySelectorAll(this.settings.cartCountSelectors),
      bubbles: this.settings.cartBubbleSelectors && document.querySelectorAll(this.settings.cartBubbleSelectors),
      offer: {
        container: this.querySelector('.cmd-offer'),
        progress: this.querySelector('.cmd-offer__progress'),
        text: this.querySelector('.cmd-offer__text'),
      },
      timer: {
        hrs: this.querySelector('#cmd-timer--hours'),
        mins: this.querySelector('#cmd-timer--minutes'),
        secs: this.querySelector('#cmd-timer--seconds'),
      },
      shipping: {
        estimate: this.querySelector('.cmd-shipping [data-shipping-estimate]'),
      },
      summary: {
        details: this.querySelector('.cmd-summary__details'),
        savings: this.querySelector('.cmd-savings'),
        terms: this.querySelector('.cmd-terms'),
        btnATC: this.querySelector('.cmd-btn'),
        btnSticky: this.querySelector('.cmd-btn__sticky'),
      },
      price: {
        subtotal: this.querySelector('.cmd-subtotal__price'),
        total: this.querySelector('.cmd-total__price'),
        delivery: this.querySelector('.cmd-delivery__price'),
        savings: this.querySelector('.cmd-savings__price'),
      },
      discount: {
        remove: this.querySelectorAll('.cmd-discount-preview__delete'),
        text: this.querySelector('.cmd-discount-field__text'),
        btn: this.querySelector('.cmd-discount-field__btn'),
        input: this.querySelector('.cmd-discount-field input'),
      },
    };
  }
  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) ON CART INIT ═══════════════════════════════════════════════ */
  onInit() {
    this.shouldCartSwap(); // swap cart variants when discount option
    this.updateOffers(); // update upsell on cart init
    this.updateMaxInventories(); // check if some items are over inventory bundle limit
    this.shippingEstimate(); // custom shipping estimate
    this.cdTimer('cart_timer'); // Countdown timer with cookie deposit
    this.iosFixes(); // ios fix when returning from checkout
  }
  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) ON USER INTERACTION ════════════════════════════════════════ */

  /**
   * Redirect to cart or checkout + animate button
   * fetch('https://domain.name/cart.json').then((resp) => resp.json()).then(console.log)
   */
  onSubmitCart() {
    const btnATC = this.DOM.summary.btnATC;
    document.addEventListener(
      'click',
      e => {
        if (!btnATC.contains(e.target)) return;
        if (this.controller.hasAttribute('data-cart-redirect')) {
          this.controller.loading = { el: btnATC, load: true };
          this.controller.loading = { el: this.DOM.summary.btnSticky, load: true };
          e.preventDefault();
          location.href = `${Shopify.routes.root}cart#cmd-ui`;
        } else if (!this.DOM.summary.terms || this.DOM.summary.terms.querySelector('input').checked) {
          if (!e.defaultPrevented) {
            this.controller.loading = { el: btnATC, load: true };
            this.controller.loading = { el: this.DOM.summary.btnSticky, load: true };
            if (this.controller.modal) this.controller.modal.overlay = { state: 'loading' };
          }
          if (this.settings.appsDisable == 'default') return;
          e.preventDefault();
          e.stopImmediatePropagation();
          location.href = `${Shopify.routes.root}checkout`;
        }
      },
      { capture: this.settings.appsDisable == 'force_2', signal: this.aborter.signal }
    );
  }

  async onSubmitGiftMessage() {
    const modal = document.querySelector('.cmd-gift-msg');
    if (!modal) return;
    await customElements.whenDefined('modal-maxdesign');
    // add or change gift message
    const upsell = modal.settings.upsell;
    const textarea = modal.querySelector('textarea');
    const btn = modal.querySelector('button');
    btn.addEventListener(
      'click',
      () => {
        const [key, value] = [upsell.propKey, textarea.value];
        const data = { id: upsell.id, properties: value.trim() === '' ? { [`_${key}`]: ' ' } : { [key]: value } };
        if (modal.details?.giftMsg) this.controller.editCart('change', data, btn);
        else this.controller.editCart('add', { items: [data] }, btn, undefined, false);
      },
      { signal: this.aborter.signal }
    );
    // restore item gift message
    document.addEventListener(
      'modal:open',
      ({ detail }) => detail === modal && (textarea.value = modal.details.giftMsg || ''),
      { signal: this.aborter.signal }
    );
  }

  async onSubmitGiftWrapping() {
    const modal = document.querySelector('.cmd-gift-wrap');
    if (!modal) return;
    await customElements.whenDefined('modal-maxdesign');
    const upsell = modal.settings.upsell;
    const controls = [...modal.querySelectorAll('.cmd-gift__qty-controls')];
    if (!controls.length && !upsell.reset) return;
    const weakMap = new WeakMap();
    const submitBtn = modal.querySelector('.cmd-gift__btn');
    const priceDisplay = submitBtn?.querySelector('[data-price]');
    const totalQty = () => controls.reduce((sum, control) => sum + weakMap.get(control).quantity, 0);
    const itemData = () => {
      const [id, quantity, properties] = [upsell.id, totalQty(), {}];
      properties[upsell.propKeys[quantity > 1 ? 'plural' : 'singular']] = controls
        .map(control => {
          const data = weakMap.get(control);
          return data.quantity && `${data.title} (${data.quantity})`;
        })
        .filter(Boolean)
        .join(' / ');
      return { id, quantity, properties };
    };
    const updateUpsell = () => {
      upsell.action == 'change'
        ? this.controller.editCart('change', itemData(), submitBtn)
        : this.controller.editCart('add', { items: [itemData()] }, submitBtn, undefined, false);
    };
    controls.forEach(control => {
      const btns = control.querySelectorAll('button');
      const input = control.querySelector('input');
      const data = { title: input.dataset.title, quantity: parseInt(input.value, 10) };
      weakMap.set(control, data);
      btns.forEach(btn =>
        btn.addEventListener(
          'click',
          e => {
            const delta = +e.target.dataset.step;
            const qty = parseInt(input.value, 10) + delta;
            btns[0].disabled = qty == 0;
            btns[1].disabled = qty == input.max;
            submitBtn.hidden = submitBtn.disabled = false;
            input.defaultValue = data.quantity = qty;
            priceDisplay.textContent = this.formatMoney(upsell.price * totalQty());
          },
          { signal: this.aborter.signal }
        )
      );
    });
    // updateUpsell() on modal close event and when a wrapped item was deleted
    upsell.reset && updateUpsell();
    submitBtn?.addEventListener('click', modal.close.bind(modal), { signal: this.aborter.signal });
    document.addEventListener(
      'modal:close',
      ({ detail }) => detail === modal && !submitBtn.disabled && updateUpsell(),
      { signal: this.aborter.signal }
    );
  }

  /**
   * Update hidden discount on input
   * Submit discount on enter key
   */
  onInputDiscount() {
    const { input, btn, text } = this.DOM.discount;
    if (!input) return;
    input.oninput = () => (input.value = input.value.toUpperCase());
    input.addEventListener(
      'keyup',
      e => {
        btn.disabled = !input.value;
        text.hidden = true;
        input.style.removeProperty('--color-discount-field');
        if (e.key === 'Enter') e.preventDefault(), btn.click();
      },
      { signal: this.aborter.signal }
    );
  }

  /**
   * Apply discount on cart page
   */
  onSubmitDiscount() {
    const { input, btn } = this.DOM.discount;
    if (!btn) return;
    btn.addEventListener(
      'click',
      () => {
        this.controller.editDiscounts({ add: input.value, btn });
      },
      { signal: this.aborter.signal }
    );
  }

  /**
   * Remove discount promotion on close action
   */
  onRemoveDiscount() {
    const remove = this.DOM.discount.remove;
    if (!remove) return;
    remove.forEach(btn =>
      btn.addEventListener(
        'click',
        () => {
          const codeToRemove = btn.parentElement.dataset.discount;
          this.controller.editDiscounts({ remove: codeToRemove, btn });
        },
        { signal: this.aborter.signal }
      )
    );
  }

  /**
   * Toggle fixed button
   */
  onATCBtnIntersect() {
    const options = {
      threshold: 0.5,
      root: location.pathname.indexOf('/cart') > -1 ? null : this.controller.modal,
    };
    new IntersectionObserver(([{ isIntersecting }]) => {
      this.toggleAttribute('data-sticky', !isIntersecting);
    }, options).observe(this.DOM.summary.btnATC);
  }

  /**
   * Delegate click to submit btn
   */
  onStickyBtnClick() {
    this.DOM.summary.btnSticky.addEventListener('click', () => this.DOM.summary.btnATC.click(), {
      signal: this.aborter.signal,
    });
  }

  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) SOLVE & SWAP CART FOR BUNDLE QTY OFFERS ════════════════════ */

  /**
   * Check if we need to swapCart()
   * Disable loader asap depending on outcome
   */
  shouldCartSwap() {
    this.controller.loading = { el: this.controller, load: this.discount.qtyBreak };
    if (!this.discount.qtyBreak) return;

    const offerItems = this.items.filter(item => item.settings.qtyBreaks.enable);
    if (offerItems.length) {
      // make group of items with same product/offer id
      const offerGroups = offerItems.reduce((obj, item) => {
        const offerId = item.settings.qtyBreaks.offerId;
        obj[offerId] = [...(obj[offerId] || []), item];
        return obj;
      }, {});
      // loop through each group, then make sets
      for (const [offerId, group] of Object.entries(offerGroups)) {
        const offerSet = group.reduce((obj, item) => {
          obj.qty = (obj.qty || 0) + item.settings.data.quantity;
          obj.discounts = (obj.discounts || []).concat([item.settings.qtyBreaks.currentValue]);
          return obj;
        }, {});
        // determine if swap cart is required
        const discountValues = group[0].settings.qtyBreaks.discountValues;
        const validDiscount = discountValues[Math.min(offerSet.qty - 1, discountValues.length - 1)];
        const needSwapCart = !offerSet.discounts.every(discount => discount == validDiscount);
        if (needSwapCart) {
          this.swapCart(discountValues, validDiscount, offerId);
          return;
        }
      }
    }
    this.controller.loading = { el: this.controller, load: false };
  }

  /**
   * permute variants with their matching discounted option
   * 1 - loop through all cart items
   * 2 - loop through their variants
   * 3 - push matching variant to array (based on new discount option)
   * 4 - push other items like before
   * 4 - clear cart and rebuild it in order
   * @param {Array} discountValues - all discount values
   * @param {string} validDiscount - new discount value to apply
   * @param {string} offerId - offerId to know what product to target
   */
  async swapCart(discountValues, validDiscount, offerId) {
    // build array of variants to add
    const variantsToAdd = this.items.map(({ settings }) => {
      const { qtyBreaks, item } = settings;
      const matchingVariant =
        qtyBreaks.offerId === offerId &&
        qtyBreaks.variants.find(
          variant =>
            variant.options.includes(validDiscount) &&
            this.newVariantMatchItem(discountValues, variant.options, item.variant_options)
        );
      return { id: matchingVariant?.id || item.id, quantity: item.quantity, properties: item.properties };
    });
    // clear cart and rebuild it
    await this.controller.clear;
    this.controller.editCart('add', { items: variantsToAdd.reverse() });
  }

  /**
   * determine whether variant is a match with main item options
   * @param {Array} discountValues - Discount values
   * @param {Array} variantOptions - Variant options
   * @param {Array} itemOptions - Current item variant options
   * @return {Boolean} - Whether new discounted variant match
   */
  newVariantMatchItem(discountValues, variantOptions, itemOptions) {
    const mainVariantOptions = variantOptions.filter(option => !discountValues.includes(option));
    const mainItemOptions = itemOptions.filter(option => !discountValues.includes(option));
    return mainVariantOptions.every((option, index) => option == mainItemOptions[index]);
  }

  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) BUNDLE VARIANT INVENTORIES ═════════════════════════════════ */

  /**
   * Update max inventories of items with same bundle ID
   * This allows to allocate one "main inventory" between all product variants
   * * This is not a 100% robust method and it must be combined with MaxDesign "Bundle Inventory Flow Action"
   * * /cart/change.js is called automatically with the observer method onInventoryMutation()
   */
  updateMaxInventories() {
    if (!this.settings.bundleInventory) return;
    // build sets of bundles
    const sets = [...this.items]
      .filter(item => item.settings.bundleInventory.enable)
      .reduce((obj, item) => {
        const { bundleInventory, data } = item.settings;
        const id = bundleInventory.bundleId;
        obj[id] = obj[id]
          ? { ...obj[id], totalQty: obj[id].totalQty + data.quantity, items: [...obj[id].items, item] }
          : { ...bundleInventory, totalQty: data.quantity, items: [item] };
        return obj;
      }, {});
    // update max value for each item
    Object.values(sets).forEach(({ totalQty, inventoryQty, items }) => {
      const delta = totalQty - inventoryQty;
      if (delta == 0) items.forEach(item => (item.qtyInput.max = item.settings.data.quantity));
      else if (delta < 0) items.forEach(item => (item.qtyInput.max = inventoryQty));
      else {
        const itemToUpdate = items.sort((a, b) => b.settings.data.quantity - a.settings.data.quantity)[0];
        const qtyMax = inventoryQty - totalQty + itemToUpdate.settings.data.quantity;
        items.forEach(item => (item.qtyInput.max = item == itemToUpdate ? qtyMax : item.settings.data.quantity));
      }
    });
  }
  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) DYNAMIC CART UPDATE ════════════════════════════════════════ */

  /**
   * update cart count textContent or data-attr for pseudo elements
   * update cart indications (bubbles, etc.)
   */
  updateCount() {
    const itemCount = this.settings.cart.item_count;
    this.DOM.counters &&
      this.DOM.counters.forEach(el => {
        const textContent = el.textContent.trim();
        if (textContent && !isNaN(textContent)) el.textContent = itemCount;
        else for (const [key, value] of Object.entries(el.dataset)) if (!isNaN(value)) el.dataset[key] = itemCount;
      });
    this.DOM.bubbles &&
      this.DOM.bubbles.forEach(el => {
        this.settings.cartBubbleOn && el.classList.toggle(this.settings.cartBubbleOn, itemCount > 0);
        this.settings.cartBubbleOff && el.classList.toggle(this.settings.cartBubbleOff, itemCount == 0);
      });
  }

  /**
   * update total price
   */
  updateTotal() {
    this.prices.total = this.prices.subtotal - this.prices.discount + this.prices.shipping;
    this.DOM.price.total.innerHTML = this.formatMoney(this.prices.total);
  }

  /**
   * Check for each step if current offer exceeds threshold
   * For shipping => threshold value must take into account discount value
   * For product => threshold value must take into account product price
   * > if step 2 qualify, congrats text
   * > if step 1 qualify, promote step 2
   * > if none qualify, promote step 1
   */
  updateOffers() {
    if (!this.offers.enable) return;
    const steps = this.offers.steps;
    let breakSignal;
    let cartThreshold = this.offers.system == 'price' ? this.prices.subtotal : this.settings.cart.item_count;

    const updateText = step => {
      const lastStepAchieved = steps[steps.length - 1].achieved;
      const goal = step[step.type].threshold - cartThreshold;
      const threshold = step.thresholdValue;
      const text = lastStepAchieved ? this.offers.qualifyText : step.text;
      this.DOM.offer.container.dataset.step = steps.filter(step => step.achieved).length;
      this.DOM.offer.text.innerHTML = text
        .replace('XX', this.offers.system == 'price' ? this.formatMoney(goal) : goal)
        .replace('YY', this.offers.system == 'price' ? this.formatMoney(threshold) : threshold);
    };

    const updateDelivery = step => {
      if (this.settings.shipping.enable) {
        const priceFromText = step.achieved
          ? step.shipping.price.replace(/[,.]/g, '')
          : this.settings.shipping.price.text.replace(/[,.]/g, '');
        this.prices.shipping = isNaN(+priceFromText) ? 0 : +priceFromText;
        this.DOM.price.delivery.innerHTML = isNaN(+priceFromText) ? priceFromText : this.formatMoney(+priceFromText);
        this.updateTotal();
      } else this.prices.shipping = 0;
    };

    const updateProduct = step => {
      const data = step.product;
      if (!data.id) return;
      const addProduct = step.achieved && !data.added;
      const removeProduct = !step.achieved && data.added && this.offers.denyProductAction == 'remove';
      if (addProduct) this.controller.editCart('add', { items: [{ id: data.id, properties: data.prop }] });
      else if (removeProduct) this.controller.editCart('change', { id: data.id, quantity: 0 });
    };

    const updateBar = step => {
      const progress = this.DOM.offer.progress;
      const thresholdValue = steps[steps.length - 1].thresholdValue;
      if (this.offers.system == 'price') {
        const subTotalProgress = cartThreshold - (step.type === 'shipping' ? this.prices.discount : 0);
        const iconProgress = this.offers.iconProgress;
        const offset = 25;
        const barProgress = Math.min((subTotalProgress * 100) / thresholdValue, 100);
        const offsetProgress = barProgress > iconProgress && barProgress - iconProgress < offset;
        const barUIProgress = offsetProgress ? iconProgress + offset : barProgress;
        progress.dataset.progress = this.formatMoney(subTotalProgress).replace(/<\/?[^>]+(>|$)/g, '');
        progress.style.setProperty('--bar-progress', `${barUIProgress}%`);
      } else {
        progress.dataset.progress = cartThreshold;
        progress.style.setProperty('--bar-progress', `${Math.min((cartThreshold * 100) / thresholdValue, 100)}%`);
      }
    };

    for (let step of steps) {
      const { type, thresholdValue, product } = step;
      const isLastStep = steps.indexOf(step) == steps.length - 1;

      switch (type) {
        case 'shipping':
          step[type].threshold = thresholdValue + (this.offers.system == 'price' ? this.prices.discount : 0);
          step.achieved = cartThreshold >= step[type].threshold;
          updateDelivery(step);
          break;
        case 'product':
          cartThreshold = [...this.items].reduce((total, { settings }) => {
            const amount = settings.data[this.offers.system == 'price' ? 'original_line_price' : 'quantity'];
            return total + (settings.offer.enable ? amount : 0);
          }, 0);
          step[type].items = this.settings.cart.items.filter(item => product.id == item.variant_id);
          step[type].added = step[type].items?.length;
          step[type].threshold = thresholdValue;
          step.achieved = cartThreshold >= step[type].threshold;
          updateProduct(step);
          break;
        default:
          step[type] = step[type] || {};
          step[type].threshold = thresholdValue;
          step.achieved = cartThreshold >= thresholdValue;
          break;
      }

      // we promote the step that is not achieved (or last step)
      if (breakSignal) break;
      if (!step.achieved || isLastStep) {
        updateText(step);
        updateBar(step);
        /* we may need to remove offered product in next step */
        if (this.offers.denyProductAction != 'remove') break;
        else breakSignal = true;
      }
    }
  }

  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) UTILITY METHODS ════════════════════════════════════════════ */

  /**
   * money formater.
   */
  formatMoney(cents) {
    const { moneyFormat } = this.settings;
    const regex = /\{\{\s*(\w+)\s*\}\}/;
    const placeholder = moneyFormat.match(regex)[1];
    const [precision, thousands, decimal] = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
    }[placeholder];
    const formatWithDelimiters = number => {
      if (isNaN(number) || number === null) return 0;
      const fixedNumber = (number / 100.0).toFixed(precision);
      const [dollars, cents] = fixedNumber.split('.');
      const thousandsRegex = /(\d)(?=(\d{3})+$)/g;
      return dollars.replace(thousandsRegex, `$1${thousands}`) + (cents ? decimal + cents : '');
    };
    const value = formatWithDelimiters(typeof cents === 'string' ? cents.replace('.', '') : cents);
    return moneyFormat.replace(regex, value);
  }

  /**
   * ios fix when returning from checkout
   */
  iosFixes() {
    addEventListener(
      'pageshow',
      event => {
        if (event.persisted) {
          this.controller.loading = { el: this.DOM.summary.btnATC, load: false };
          this.controller.loading = { el: this.DOM.summary.btnSticky, load: false };
        }
      },
      { signal: this.aborter.signal }
    );
  }

  /**
   * Estimate shipping time in custom block
   */
  shippingEstimate() {
    if (!this.settings.shipping.estimate.enable) return;
    const { estimate } = this.DOM.shipping;
    if (!estimate) return;
    const { delayMin, delayMax, weekday, skipSat, skipSun, skipHol, language, country } =
      this.settings.shipping.estimate;
    const date = new Date();
    const year = date.getFullYear();
    const locales = `${language}-${country}`;

    const addDays = (date, days) => {
      const newDate = new Date(date);
      while (days > 0) {
        newDate.setDate(newDate.getDate() + 1);
        const dayIndex = newDate.getDay();
        if ((skipSun && dayIndex === 0) || (skipSat && dayIndex === 6)) continue;
        days--;
      }
      return newDate;
    };

    const getDay = (date, holidays) => {
      const isHoliday = holidays?.some(item => item.date == date.toISOString().slice(0, 10));
      return isHoliday ? addDays(date, 1) : date;
    };

    const fetchHolidays = async () => {
      const response = await Promise.race([
        fetch(`https://date.nager.at/api/v3/publicholidays/${year}/${country}`),
        new Promise((_, reject) => setTimeout(() => reject(new Error('date.nager api fetch request timed out')), 300)),
      ]);
      const holidays = await response.json();
      if (holidays.errors) throw holidays.errors;
      return holidays;
    };

    const getDeliveryDate = async () => {
      let start, end;
      try {
        const holidays = skipHol ? await fetchHolidays() : null;
        start = getDay(addDays(date, delayMin), holidays);
        end = getDay(addDays(date, delayMax), holidays);
      } catch (error) {
        console.error(error);
        start = getDay(addDays(date, delayMin));
        end = getDay(addDays(date, delayMax));
      }
      const formatOptions = {
        weekday: weekday ? 'short' : undefined,
        day: '2-digit',
        month: start.getMonth() != end.getMonth() || weekday ? 'short' : undefined,
      };
      const startFormat = start.toLocaleDateString(locales, formatOptions);
      const endFormat = end.toLocaleDateString(locales, { ...formatOptions, month: 'short' });
      return start.getDate() != end.getDate() ? `${startFormat} - ${endFormat}` : `${endFormat}`;
    };

    getDeliveryDate().then(result => (estimate.textContent = result));
  }

  /**
   * run cdTimer with cookies
   */
  cdTimer(name) {
    if (!this.settings.cdTimer.enable) return;
    const cookieDeadline = this.cookie(name);
    const initialDeadline = Date.now() + this.settings.cdTimer.time * 60000;
    const cookieExpire = new Date(initialDeadline + this.settings.cdTimer.expiry * 60000).toUTCString();
    const deadline = cookieDeadline || initialDeadline;

    // write cookie
    if (!cookieDeadline) this.cookie = { key: name, value: deadline, expires: cookieExpire };

    // init timer
    const updateClock = () => {
      if (this.aborter.signal.aborted) return;
      const total = deadline - Date.now();
      const time = {
        hrs: Math.floor((total / (1000 * 60 * 60)) % 24),
        mins: Math.floor((total / 1000 / 60) % 60),
        secs: Math.floor((total / 1000) % 60),
      };
      if (total > 0 && !isNaN(total)) {
        this.DOM.timer.hrs.innerHTML = time.hrs == 0 ? '' : `${('0' + time.hrs).slice(-2)}&thinsp;:&thinsp;`;
        this.DOM.timer.mins.innerHTML = `${('0' + time.mins).slice(-2)}&thinsp;:&thinsp;`;
        this.DOM.timer.secs.innerHTML = ('0' + time.secs).slice(-2);
        setTimeout(updateClock.bind(this), 1000);
        this.setAttribute('data-timer', '');
      } else this.removeAttribute('data-timer');
    };
    updateClock();
  }

  /* #endregion ════════════════════════════════════════════════════════════════════ */

  /* #region(collapsed) GETTERS & SETTERS ══════════════════════════════════════════ */

  /**
   * get cookie value by name
   * @param {string} name - name of cookie (key)
   */
  get cookie() {
    return name => {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    };
  }

  /**
   * Set cookie
   * @param {string} key
   * @param {string} value
   * @param {string} expires - default to remove
   */
  set cookie({ key, value, expires = 'Thu, 01 Jan 1970 00:00:00 UTC' }) {
    return (document.cookie = `${key}=${value}; expires=${expires}; path=/`);
  }

  /* #endregion ════════════════════════════════════════════════════════════════════ */
}
/* #endregion ════════════════════════════════════════════════════════════════════ */

/* #region CART ITEMS ════════════════════════════════════════════════════════════ */
class CartItem extends HTMLElement {
  connectedCallback() {
    this.aborter = new AbortController();
    this.cart = MaxDesign.cart.UI;
    this.controller = this.cart.controller;
    this.cart.items = this.cart.items || [];
    this.cart.items.push(this);
    this.settings = this.controller.json(this);
    this.qtyInput = this.querySelector('.cmd-item__qty input');
    this.qtyBtns = this.querySelectorAll('.cmd-item__qty button');
    this.deleters = this.querySelectorAll('.cmd-item__delete');
    this.groupEditer = this.querySelector('.cmd-item__qty-edit[data-edit-group]');
    this.bundleInventory = this.settings.bundleInventory.enable && this.cart.settings.bundleInventory;
    this.onInventoryMutation();
    this.onUpdateQty();
    this.onProductGroupEdit();
  }

  disconnectedCallback() {
    this.aborter.abort();
    this.cart.updateCount();
  }

  /**
   * Update cart quantity on quantity click
   * 1 > Mobile selector
   * 2 > Increment / decrement buttons
   * 3 > Bin / cross
   */
  onUpdateQty() {
    this.qtyBtns.forEach(button => {
      button.addEventListener(
        'click',
        e => {
          if (this.preventClick) return;
          const target = e.target;
          const delta = +target.dataset.step;
          const qty = parseInt(this.qtyInput.value, 10) + delta;
          const qtyMax = Math.max(0, +this.qtyInput.max);
          this.qtyInput.defaultValue = Math.min(qty, qtyMax ? qtyMax : qty);
          this.bundleInventory && this.cart.updateMaxInventories();
          this.preventIncrement = qtyMax && qty >= qtyMax;
        },
        { signal: this.aborter.signal }
      );
    });
    this.deleters.forEach(el => {
      el.addEventListener(
        'click',
        e => {
          e.preventDefault();
          e.stopImmediatePropagation();
          let keys = this.settings.productGroup.keys;
          const updates = keys.split(',').reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
          this.controller.editCart('update', { updates });
        },
        { signal: this.aborter.signal }
      );
    });
  }

  /**
   * Observe input value change
   * Update cart item quantity in case max limit is exceeded
   */
  onInventoryMutation() {
    new MutationObserver(mutations => {
      mutations.forEach(({ attributeName, target }) => {
        const [currValue, maxValue] = [+target.value, +target.max];
        if (attributeName == 'value') {
          this.controller.editCart('change', { id: this.settings.data.key, quantity: currValue });
        } else if (attributeName == 'max') {
          this.preventIncrement = currValue >= maxValue;
          if (currValue > maxValue) target.defaultValue = maxValue;
        }
      });
    }).observe(this.qtyInput, { attributeFilter: ['value', 'max'] });
  }

  onProductGroupEdit() {
    const link = this.groupEditer;
    link?.addEventListener('click', e => {
      e.preventDefault();
      const id = this.settings.productGroup.id;
      const variants = this.cart.items
        .filter(item => item.settings.productGroup.id == id && item.settings.data.properties['_option'])
        .map(item => item.settings.data);
      const url = new URL(link.href);
      url.searchParams.append('group', id);
      localStorage.setItem('productGroup', JSON.stringify(variants));
      location.href = url.href;
    });
  }

  /**
   * Prevents to increment quantities when out of stock
   * @param {Boolean} status
   */
  set preventIncrement(status) {
    this.qtyInput.dataset.increment = status ? 'deny' : 'allow';
  }

  /**
   * Prevent too many clicks on quantity buttons
   * @returns {boolean} - Returns true if the click is prevented, false otherwise
   */
  get preventClick() {
    const now = performance.now();
    if (now - this.lastClick < 400) {
      console.warn('Too many simultaneous requests');
      return true;
    }
    this.lastClick = now;
    return false;
  }
}
/* #endregion ════════════════════════════════════════════════════════════════════ */

/* #region CART RECOMMENDATIONS ══════════════════════════════════════════════════ */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.controller = MaxDesign.cart.controller;
    this.settings = this.controller.json(this);
    this.section = this.closest('.shopify-section');
    this.placeholder = '[data-recommendations-result]';
    this.result = this.querySelector(this.placeholder);
    Object.assign(this, this.settings);
    this.loadRecommendations();
  }

  disconnectedCallback() {
    if (MaxDesign?.cart?.UI?.querySelector('.cmd-empty'))
      this.controller.modal?.removeAttribute('data-recommendations');
  }

  async loadRecommendations() {
    try {
      const url = `${Shopify.routes.root}recommendations/products?product_id=${this.productId}&limit=${this.limit}&section_id=${this.sectionId}`;
      const [resp1, resp2] = await Promise.all([fetch(`${url}&intent=complementary`), fetch(`${url}&intent=related`)]);
      const recom1 = new DOMParser().parseFromString(await resp1.text(), 'text/html').querySelector(this.placeholder);
      const recom2 = new DOMParser().parseFromString(await resp2.text(), 'text/html').querySelector(this.placeholder);
      recom1.append(...recom2.childNodes);
      if (recom1.childElementCount) {
        this.result.replaceWith(recom1);
        [...recom1.children].slice(this.limit).forEach(child => child.remove());
        const totalRecom = recom1.childElementCount + recom1.parentElement.childElementCount - 2;
        const columns = getComputedStyle(this).getPropertyValue('--recommendations-columns') || 3;
        this.style.setProperty('--rows', Math.max(1, Math.floor(totalRecom / columns)));
        this.recommendations = true;
      } else {
        this.recommendations = false;
        this.remove();
      }
    } catch (error) {
      console.error(error);
    }
  }

  set recommendations(open) {
    this.toggleAttribute('data-performed', open);
    this.controller.modal?.toggleAttribute('data-recommendations', open);
  }
}
/* #endregion ════════════════════════════════════════════════════════════════════ */

/* #region REGISTER ELEMENTS ═════════════════════════════════════════════════════ */
customElements.define('cart-controller-maxdesign', CartController);
customElements.define('cart-ui-maxdesign', CartUI);
customElements.define('cart-item-maxdesign', CartItem);
customElements.define('cart-recommendations-maxdesign', CartRecommendations);
/* #endregion ════════════════════════════════════════════════════════════════════ */
